/**
 * Clinical Automation Dashboard
 * 
 * Master control panel for the Hospice Clinical Automation Agent.
 * Provides visibility into compliance status, mortality alerts, signature queues,
 * and automated clinical processes per CMS CoP and CHAP standards.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useTheme } from "@/contexts/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertTriangle, CheckCircle, Clock, FileSignature, Users, 
  Stethoscope, Shield, TrendingUp, AlertCircle, RefreshCw,
  FileText, Heart, Activity, Calendar, Loader2, XCircle,
  ChevronRight, Eye, ClipboardList, Brain, Zap, Bell
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge colors
const STATUS_COLORS = {
  compliant: "bg-green-500/20 text-green-400 border-green-500/30",
  at_risk: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  non_compliant: "bg-red-500/20 text-red-400 border-red-500/30",
  signed: "bg-green-500/20 text-green-400 border-green-500/30",
  pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  overdue: "bg-red-500/20 text-red-400 border-red-500/30",
  critical: "bg-red-500/20 text-red-400 border-red-500/30",
  high: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  moderate: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  low: "bg-green-500/20 text-green-400 border-green-500/30"
};

export default function ClinicalAutomationDashboard() {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [executiveDashboard, setExecutiveDashboard] = useState(null);
  const [admissionCompliance, setAdmissionCompliance] = useState(null);
  const [pocQueue, setPocQueue] = useState(null);
  const [idgNotes, setIdgNotes] = useState(null);
  const [mortalityAlerts, setMortalityAlerts] = useState(null);
  const [surveyReadiness, setSurveyReadiness] = useState(null);
  
  const token = localStorage.getItem("token");
  
  // Fetch executive dashboard
  const fetchExecutiveDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/clinical-automation/executive-dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setExecutiveDashboard(data);
      }
    } catch (error) {
      console.error("Dashboard fetch error:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);
  
  // Fetch admission compliance
  const fetchAdmissionCompliance = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/clinical-automation/admission/compliance-dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAdmissionCompliance(data);
      }
    } catch (error) {
      console.error("Admission compliance fetch error:", error);
    }
  }, [token]);
  
  // Fetch POC signature queue
  const fetchPOCQueue = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/clinical-automation/poc/signature-queue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPocQueue(data);
      }
    } catch (error) {
      console.error("POC queue fetch error:", error);
    }
  }, [token]);
  
  // Fetch IDG notes pending signature
  const fetchIDGNotes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/clinical-automation/idg/notes-pending-signature`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIdgNotes(data);
      }
    } catch (error) {
      console.error("IDG notes fetch error:", error);
    }
  }, [token]);
  
  // Fetch mortality alerts
  const fetchMortalityAlerts = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/clinical-automation/mortality-risk/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMortalityAlerts(data);
      }
    } catch (error) {
      console.error("Mortality alerts fetch error:", error);
    }
  }, [token]);
  
  // Fetch survey readiness
  const fetchSurveyReadiness = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/clinical-automation/survey-readiness/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSurveyReadiness(data);
      }
    } catch (error) {
      console.error("Survey readiness fetch error:", error);
    }
  }, [token]);
  
  // Initial fetch
  useEffect(() => {
    fetchExecutiveDashboard();
  }, [fetchExecutiveDashboard]);
  
  // Fetch tab-specific data
  useEffect(() => {
    if (activeTab === "admission") fetchAdmissionCompliance();
    if (activeTab === "poc") fetchPOCQueue();
    if (activeTab === "idg") fetchIDGNotes();
    if (activeTab === "mortality") fetchMortalityAlerts();
    if (activeTab === "survey") fetchSurveyReadiness();
  }, [activeTab, fetchAdmissionCompliance, fetchPOCQueue, fetchIDGNotes, fetchMortalityAlerts, fetchSurveyReadiness]);
  
  // Run admission compliance check
  const runAdmissionCheck = async (patientId) => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/clinical-automation/admission/compliance-check`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ patient_id: patientId })
      });
      if (res.ok) {
        toast.success("Compliance check completed");
        fetchAdmissionCompliance();
        fetchExecutiveDashboard();
      }
    } catch (error) {
      toast.error("Compliance check failed");
    } finally {
      setLoading(false);
    }
  };
  
  // Batch sign POCs
  const batchSignPOC = async (physicianId) => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/clinical-automation/poc/batch-sign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ physician_id: physicianId })
      });
      if (res.ok) {
        toast.success("Batch signature initiated");
        fetchPOCQueue();
        fetchExecutiveDashboard();
      }
    } catch (error) {
      toast.error("Batch sign failed");
    } finally {
      setLoading(false);
    }
  };
  
  const getStatusBadge = (status) => {
    return STATUS_COLORS[status] || STATUS_COLORS.moderate;
  };

  return (
    <div className={`min-h-screen p-6 ${isDark ? "bg-slate-950 text-white" : "bg-gray-50 text-gray-900"}`} data-testid="clinical-automation-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Clinical Automation</h1>
            <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              CMS CoP & CHAP Compliance Engine
            </p>
          </div>
        </div>
        <Button 
          onClick={() => {
            fetchExecutiveDashboard();
            toast.success("Dashboard refreshed");
          }}
          disabled={loading}
          variant="outline"
          data-testid="refresh-automation-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Executive Summary Cards */}
      {executiveDashboard && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          {/* Admission Compliance */}
          <Card className={`${isDark ? "bg-slate-900 border-slate-800" : "bg-white"} cursor-pointer hover:shadow-lg transition-shadow`} onClick={() => setActiveTab("admission")}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <ClipboardList className="w-5 h-5 text-blue-400" />
                <Badge variant="outline" className={getStatusBadge(
                  executiveDashboard.admission_compliance?.compliance_percentage >= 90 ? "compliant" :
                  executiveDashboard.admission_compliance?.compliance_percentage >= 70 ? "at_risk" : "non_compliant"
                )}>
                  {executiveDashboard.admission_compliance?.compliance_percentage || 0}%
                </Badge>
              </div>
              <p className="text-2xl font-bold">{executiveDashboard.admission_compliance?.total || 0}</p>
              <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>48-Hour Admissions</p>
              <div className="mt-2 flex gap-2 text-xs">
                <span className="text-green-400">{executiveDashboard.admission_compliance?.rates?.compliant || 0} ✓</span>
                <span className="text-yellow-400">{executiveDashboard.admission_compliance?.rates?.at_risk || 0} ⚠</span>
                <span className="text-red-400">{executiveDashboard.admission_compliance?.rates?.non_compliant || 0} ✗</span>
              </div>
            </CardContent>
          </Card>

          {/* Mortality Alerts */}
          <Card className={`${isDark ? "bg-slate-900 border-slate-800" : "bg-white"} cursor-pointer hover:shadow-lg transition-shadow`} onClick={() => setActiveTab("mortality")}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Heart className="w-5 h-5 text-red-400" />
                {executiveDashboard.mortality_risk?.critical > 0 && (
                  <Badge className="bg-red-500/20 text-red-400 animate-pulse">CRITICAL</Badge>
                )}
              </div>
              <p className="text-2xl font-bold">{executiveDashboard.mortality_risk?.pending_alerts || 0}</p>
              <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>Mortality Alerts</p>
              <div className="mt-2 flex gap-2 text-xs">
                <span className="text-red-400">{executiveDashboard.mortality_risk?.critical || 0} Critical</span>
                <span className="text-orange-400">{executiveDashboard.mortality_risk?.high || 0} High</span>
              </div>
            </CardContent>
          </Card>

          {/* POC Signatures */}
          <Card className={`${isDark ? "bg-slate-900 border-slate-800" : "bg-white"} cursor-pointer hover:shadow-lg transition-shadow`} onClick={() => setActiveTab("poc")}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <FileSignature className="w-5 h-5 text-purple-400" />
                {executiveDashboard.poc_signatures?.total_unsigned > 0 && (
                  <Badge className="bg-yellow-500/20 text-yellow-400">Pending</Badge>
                )}
              </div>
              <p className="text-2xl font-bold">{executiveDashboard.poc_signatures?.total_unsigned || 0}</p>
              <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>Unsigned POCs</p>
              <p className="mt-2 text-xs text-purple-400">
                {executiveDashboard.poc_signatures?.pending_batches || 0} batch{executiveDashboard.poc_signatures?.pending_batches !== 1 ? 'es' : ''} queued
              </p>
            </CardContent>
          </Card>

          {/* Survey Readiness */}
          <Card className={`${isDark ? "bg-slate-900 border-slate-800" : "bg-white"} cursor-pointer hover:shadow-lg transition-shadow`} onClick={() => setActiveTab("survey")}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Shield className="w-5 h-5 text-cyan-400" />
                {executiveDashboard.survey_readiness?.high_risk_charts > 0 && (
                  <Badge className="bg-red-500/20 text-red-400">At Risk</Badge>
                )}
              </div>
              <p className="text-2xl font-bold">{executiveDashboard.survey_readiness?.high_risk_charts || 0}</p>
              <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>High-Risk Charts</p>
              <p className="mt-2 text-xs text-orange-400">
                {executiveDashboard.survey_readiness?.adr_exposure || 0} ADR exposure
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`grid grid-cols-6 mb-4 ${isDark ? "bg-slate-900" : "bg-gray-100"}`}>
          <TabsTrigger value="overview" className="text-xs" data-testid="overview-tab">
            <Activity className="w-3 h-3 mr-1" /> Overview
          </TabsTrigger>
          <TabsTrigger value="admission" className="text-xs" data-testid="admission-tab">
            <ClipboardList className="w-3 h-3 mr-1" /> 48-Hour
          </TabsTrigger>
          <TabsTrigger value="poc" className="text-xs" data-testid="poc-tab">
            <FileSignature className="w-3 h-3 mr-1" /> POC Sign
          </TabsTrigger>
          <TabsTrigger value="idg" className="text-xs" data-testid="idg-tab">
            <Users className="w-3 h-3 mr-1" /> IDG Notes
          </TabsTrigger>
          <TabsTrigger value="mortality" className="text-xs" data-testid="mortality-tab">
            <Heart className="w-3 h-3 mr-1" /> Mortality
          </TabsTrigger>
          <TabsTrigger value="survey" className="text-xs" data-testid="survey-tab">
            <Shield className="w-3 h-3 mr-1" /> Survey
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <OverviewTab 
            executiveDashboard={executiveDashboard} 
            loading={loading} 
            isDark={isDark} 
          />
        </TabsContent>

        {/* Admission Compliance Tab */}
        <TabsContent value="admission">
          <AdmissionComplianceTab 
            data={admissionCompliance}
            loading={loading}
            isDark={isDark}
            onRunCheck={runAdmissionCheck}
            onRefresh={fetchAdmissionCompliance}
          />
        </TabsContent>

        {/* POC Signature Tab */}
        <TabsContent value="poc">
          <POCSignatureTab 
            data={pocQueue}
            loading={loading}
            isDark={isDark}
            onBatchSign={batchSignPOC}
            onRefresh={fetchPOCQueue}
          />
        </TabsContent>

        {/* IDG Notes Tab */}
        <TabsContent value="idg">
          <IDGNotesTab 
            data={idgNotes}
            loading={loading}
            isDark={isDark}
            onRefresh={fetchIDGNotes}
          />
        </TabsContent>

        {/* Mortality Risk Tab */}
        <TabsContent value="mortality">
          <MortalityRiskTab 
            data={mortalityAlerts}
            loading={loading}
            isDark={isDark}
            onRefresh={fetchMortalityAlerts}
            token={token}
          />
        </TabsContent>

        {/* Survey Readiness Tab */}
        <TabsContent value="survey">
          <SurveyReadinessTab 
            data={surveyReadiness}
            loading={loading}
            isDark={isDark}
            onRefresh={fetchSurveyReadiness}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ==================== SUB-COMPONENTS ====================

function OverviewTab({ executiveDashboard, loading, isDark }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Scheduler Status */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Clock className="w-4 h-4 text-cyan-400" />
            Automated Jobs Status
          </CardTitle>
          <CardDescription>Background automation tasks running 24/7</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-4">
            {[
              { name: "Mortality Surveillance", time: "2:00 AM", status: "active" },
              { name: "Admission Compliance", time: "Every 4 hrs", status: "active" },
              { name: "POC Signature Check", time: "7:00 AM", status: "active" },
              { name: "Psychosocial Analysis", time: "Mon 6:00 AM", status: "active" },
              { name: "Survey Readiness", time: "5:00 AM", status: "active" },
            ].map((job, i) => (
              <div key={i} className={`p-3 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-xs font-medium">{job.name}</span>
                </div>
                <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>{job.time}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-3 gap-4">
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {executiveDashboard?.admission_compliance?.total || 0}
                </p>
                <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  Active Admissions (48h)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <FileSignature className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {executiveDashboard?.poc_signatures?.total_unsigned || 0}
                </p>
                <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  Pending Signatures
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {(executiveDashboard?.mortality_risk?.critical || 0) + (executiveDashboard?.mortality_risk?.high || 0)}
                </p>
                <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  Critical/High Risk
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CMS CoP Checklist */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Shield className="w-4 h-4 text-green-400" />
            CMS Conditions of Participation Compliance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            {[
              { name: "48-Hour Comprehensive Assessment", status: "monitored" },
              { name: "IDG Meetings (Every 15 Days)", status: "monitored" },
              { name: "Plan of Care Signatures", status: "monitored" },
              { name: "Physician F2F Encounters", status: "monitored" },
              { name: "Aide Supervision (14 Days)", status: "monitored" },
              { name: "CTI/Recertification", status: "monitored" },
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-between p-2 rounded bg-green-500/10 border border-green-500/20">
                <span className="text-sm">{item.name}</span>
                <Badge className="bg-green-500/20 text-green-400">Active</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AdmissionComplianceTab({ data, loading, isDark, onRunCheck, onRefresh }) {
  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">48-Hour Admission Compliance</h3>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-4 gap-4">
        <Card className={isDark ? "bg-slate-800 border-slate-700" : "bg-gray-50"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold">{data?.total || 0}</p>
            <p className="text-xs text-gray-500">Total Admissions</p>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-green-900/20 border-green-500/30" : "bg-green-50"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-400">{data?.compliant || 0}</p>
            <p className="text-xs text-green-500">Compliant</p>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-yellow-900/20 border-yellow-500/30" : "bg-yellow-50"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-yellow-400">{data?.at_risk || 0}</p>
            <p className="text-xs text-yellow-500">At Risk</p>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-red-900/20 border-red-500/30" : "bg-red-50"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-400">{data?.non_compliant || 0}</p>
            <p className="text-xs text-red-500">Non-Compliant</p>
          </CardContent>
        </Card>
      </div>

      {/* Requirements Checklist */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm">48-Hour Requirements</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2">
            {[
              "RN Initial Assessment",
              "Comprehensive Assessment",
              "Medication Reconciliation",
              "Pain & Symptom Assessment",
              "Initial Care Plan",
              "Hospice Election Statement (HES)",
              "Certificate of Terminal Illness (CTI)"
            ].map((req, i) => (
              <div key={i} className={`flex items-center gap-2 p-2 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
                <CheckCircle className="w-4 h-4 text-cyan-400" />
                <span className="text-sm">{req}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Patient List */}
      {data?.patients && data.patients.length > 0 && (
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardHeader>
            <CardTitle className="text-sm">Recent Admissions</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-64">
              {data.patients.map((patient, i) => (
                <div key={i} className={`flex items-center justify-between p-3 mb-2 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
                  <div>
                    <p className="font-medium">{patient.patient_name}</p>
                    <p className="text-xs text-gray-500">
                      Admitted: {new Date(patient.admission_date).toLocaleDateString()} | 
                      {patient.hours_remaining > 0 ? ` ${patient.hours_remaining}h remaining` : " Overdue"}
                    </p>
                  </div>
                  <Badge className={STATUS_COLORS[patient.status]}>
                    {patient.status}
                  </Badge>
                </div>
              ))}
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {(!data?.patients || data.patients.length === 0) && (
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
            <p className="text-lg font-medium">All Clear</p>
            <p className="text-sm text-gray-500">No admissions requiring attention</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function POCSignatureTab({ data, loading, isDark, onBatchSign, onRefresh }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Plan of Care Signature Queue</h3>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Physician Batches */}
      {data?.batches && data.batches.length > 0 ? (
        <div className="space-y-3">
          {data.batches.map((batch, i) => (
            <Card key={i} className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <Stethoscope className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="font-medium">{batch.physician_name}</p>
                      <p className="text-xs text-gray-500">{batch.unsigned_count} unsigned POCs</p>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => onBatchSign(batch.physician_id)}
                    disabled={loading}
                  >
                    <FileSignature className="w-4 h-4 mr-1" />
                    Batch Sign
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
            <p className="text-lg font-medium">All POCs Signed</p>
            <p className="text-sm text-gray-500">No pending signatures</p>
          </CardContent>
        </Card>
      )}

      {/* Signature Timeline Requirements */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm">POC Signature Requirements</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className={`p-3 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-blue-400" />
                <span className="font-medium">Initial POC</span>
              </div>
              <p className="text-xs text-gray-500">Physician signature within 5 days of admission</p>
            </div>
            <div className={`p-3 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-purple-400" />
                <span className="font-medium">Ongoing POC</span>
              </div>
              <p className="text-xs text-gray-500">Re-signed every 15 days after IDG review</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function IDGNotesTab({ data, loading, isDark, onRefresh }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">IDG Notes Pending Signature</h3>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* By Discipline */}
      {data?.by_discipline && Object.keys(data.by_discipline).length > 0 ? (
        <div className="grid grid-cols-4 gap-4">
          {Object.entries(data.by_discipline).map(([discipline, count]) => (
            <Card key={discipline} className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold">{count}</p>
                <p className="text-xs text-gray-500 capitalize">{discipline}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
            <p className="text-lg font-medium">All IDG Notes Signed</p>
            <p className="text-sm text-gray-500">No pending signatures</p>
          </CardContent>
        </Card>
      )}

      {/* Notes List */}
      {data?.notes && data.notes.length > 0 && (
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardHeader>
            <CardTitle className="text-sm">Pending Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-64">
              {data.notes.map((note, i) => (
                <div key={i} className={`flex items-center justify-between p-3 mb-2 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
                  <div>
                    <p className="font-medium">{note.patient_name}</p>
                    <p className="text-xs text-gray-500">
                      {note.discipline} | IDG: {new Date(note.idg_date).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge className={note.days_pending > 3 ? STATUS_COLORS.overdue : STATUS_COLORS.pending}>
                    {note.days_pending} days
                  </Badge>
                </div>
              ))}
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function MortalityRiskTab({ data, loading, isDark, onRefresh, token }) {
  const [assessing, setAssessing] = useState(false);
  const [patientId, setPatientId] = useState("");

  const assessPatient = async () => {
    if (!patientId) return;
    try {
      setAssessing(true);
      const res = await fetch(`${API_URL}/api/clinical-automation/mortality-risk/assess`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ patient_id: patientId })
      });
      if (res.ok) {
        toast.success("Mortality assessment complete");
        onRefresh();
      }
    } catch (error) {
      toast.error("Assessment failed");
    } finally {
      setAssessing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">High Mortality Risk Surveillance</h3>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Manual Assessment */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm">Manual Risk Assessment</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input 
              placeholder="Enter Patient ID" 
              value={patientId}
              onChange={(e) => setPatientId(e.target.value)}
              className={isDark ? "bg-slate-800 border-slate-700" : ""}
            />
            <Button onClick={assessPatient} disabled={assessing || !patientId}>
              {assessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4 mr-1" />}
              Assess
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Alerts List */}
      {data?.alerts && data.alerts.length > 0 ? (
        <div className="space-y-3">
          {data.alerts.map((alert, i) => (
            <Card key={i} className={`border-l-4 ${
              alert.risk_level === "critical" ? "border-l-red-500" :
              alert.risk_level === "high" ? "border-l-orange-500" : "border-l-yellow-500"
            } ${isDark ? "bg-slate-900 border-slate-800" : "bg-white"}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className={`w-5 h-5 ${
                        alert.risk_level === "critical" ? "text-red-400" :
                        alert.risk_level === "high" ? "text-orange-400" : "text-yellow-400"
                      }`} />
                      <span className="font-semibold">{alert.patient_name}</span>
                      <Badge className={STATUS_COLORS[alert.risk_level]}>
                        {alert.risk_level.toUpperCase()}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 mb-2">
                      {alert.days_to_bp_end} days to benefit period end
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {alert.indicators?.map((ind, j) => (
                        <Badge key={j} variant="outline" className="text-xs">
                          {ind.indicator}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">
                      {new Date(alert.created_at).toLocaleDateString()}
                    </p>
                    {alert.physician_alert_sent && (
                      <Badge className="mt-1 bg-green-500/20 text-green-400">MD Alerted</Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
            <p className="text-lg font-medium">No Active Alerts</p>
            <p className="text-sm text-gray-500">Surveillance running nightly at 2 AM</p>
          </CardContent>
        </Card>
      )}

      {/* Risk Indicators Reference */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm">Monitored Risk Indicators</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {[
              "PPS Decline (>10 points)",
              "Escalating Symptom Burden",
              "Increased PRN Use",
              "Reduced Oral Intake",
              "Functional Decline",
              "Caregiver Distress",
              "Recurrent Infections",
              "Altered Mental Status"
            ].map((indicator, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-red-400" />
                <span>{indicator}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SurveyReadinessTab({ data, loading, isDark, onRefresh }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Survey & ADR Readiness</h3>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Risk Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card className={isDark ? "bg-red-900/20 border-red-500/30" : "bg-red-50"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-400">{data?.high_risk_charts || 0}</p>
            <p className="text-xs text-red-500">High-Risk Charts</p>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-orange-900/20 border-orange-500/30" : "bg-orange-50"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-orange-400">{data?.adr_exposure || 0}</p>
            <p className="text-xs text-orange-500">ADR Exposure</p>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-yellow-900/20 border-yellow-500/30" : "bg-yellow-50"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-yellow-400">{data?.late_documentation || 0}</p>
            <p className="text-xs text-yellow-500">Late Documentation</p>
          </CardContent>
        </Card>
      </div>

      {/* Discipline Gaps */}
      {data?.discipline_gaps && Object.keys(data.discipline_gaps).length > 0 && (
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardHeader>
            <CardTitle className="text-sm">Compliance Gaps by Discipline</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(data.discipline_gaps).map(([discipline, gaps]) => (
                <div key={discipline} className={`p-3 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium capitalize">{discipline}</span>
                    <Badge className="bg-red-500/20 text-red-400">{gaps.count} gaps</Badge>
                  </div>
                  <Progress value={100 - (gaps.count * 10)} className="h-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* What Surveyors Would Ask */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Eye className="w-4 h-4 text-cyan-400" />
            What Surveyors Would Ask
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            {[
              "Show me your 48-hour comprehensive assessments",
              "Where is the physician signature on this POC?",
              "When was the last IDG meeting for this patient?",
              "Can you show the aide supervision documentation?",
              "Where is the F2F encounter for this recertification?",
              "Show me your medication reconciliation process"
            ].map((question, i) => (
              <div key={i} className={`p-2 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
                <span className="text-cyan-400 mr-2">Q:</span>
                {question}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* No Issues State */}
      {(!data?.high_risk_charts || data.high_risk_charts === 0) && (
        <Card className={`${isDark ? "bg-green-900/20 border-green-500/30" : "bg-green-50"}`}>
          <CardContent className="p-6 text-center">
            <Shield className="w-12 h-12 mx-auto mb-4 text-green-400" />
            <p className="text-lg font-medium text-green-400">Survey Ready</p>
            <p className="text-sm text-gray-500">No critical compliance gaps detected</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
